import React from "react";
import { Player } from "../../store/players/models";
import { Match } from "../../store/matches/models";
import {
  PlayerDescriptor,
  League,
  LeaguePlayer,
  LeagueAccess,
  Clubs,
} from "../../store/leagues/models";
import {
  Dialog,
  DialogTitle,
  Grid,
  Hidden,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogActions,
  Button,
} from "@material-ui/core";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import ReactGA from "react-ga";
import Alert from "@material-ui/lab/Alert";

interface CreateMatchDialogProps {
  open: boolean;
  player: Player | PlayerDescriptor;
  oponent: LeaguePlayer;
  league: League;
  onClosed: (success: boolean, match: Match | null) => void;
}

const CreateMatchDialog: React.FC<CreateMatchDialogProps> = ({
  open,
  player,
  oponent,
  league,
  onClosed,
}) => {
  const handleClose = () => {
    onClosed(false, null);
  };

  const handleSubmit = () => {
    if (!dateOfMatch || !club) {
      return;
    }

    const match: Match = {
      club,
      date: dateOfMatch,
      player: {
        id: player.id!,
        level: player.level || "",
        name: player.name!,
        surname: player.surname!,
        isChildAccount: false
      },
      oponent,
      isOponentRetired: false
    };

    onClosed(true, match);
  };

  const handleClubChange = (evt: any) => {
    const clubId = evt.target.value;

    const club =
      league.type.access === LeagueAccess.Public ||
        league.type.access === LeagueAccess.Tournament
        ? league.type.supportedClubs.find((x) => x.id === clubId)
        : league.type.owner;

    setClub(club);
  };

  const [club, setClub] = React.useState<Clubs | undefined>();
  const [dateOfMatch, setDateOfMatch] = React.useState<
    Date | undefined | null
  >();

  React.useEffect(() => {
    let currentClub: Clubs;

    switch (league.type.access) {
      case LeagueAccess.Public:
      case LeagueAccess.Tournament: {
        currentClub = league.type.supportedClubs[0];
        break;
      }
      case LeagueAccess.OwnedByClub: {
        currentClub = league.type.owner;
      }
    }

    setClub(currentClub);

    setDateOfMatch(new Date());
  }, []);

  React.useEffect(() => {
    if (open) {
      ReactGA.modalview("Create match dialog");
    }
  }, [open]);

  return (
    <form autoComplete="off" noValidate>
      <Dialog open={open}>
        <DialogTitle>Zaplanuj mecz</DialogTitle>
        <div style={{ padding: "24px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                value={`${player.name} ${player.surname}`}
                label="Zawodnik"
                variant="outlined"
                InputProps={{ readOnly: true }}
                fullWidth
              ></TextField>
            </Grid>
            <Hidden only={["xs", "sm"]}>
              <Grid item md={2}>
                <Typography variant="h4" style={{ textAlign: "center" }}>
                  vs.
                </Typography>
              </Grid>
            </Hidden>
            <Grid item xs={12} md={5}>
              <TextField
                value={`${oponent.name} ${oponent.surname}`}
                fullWidth
                label="Przeciwnik"
                variant="outlined"
                InputProps={{ readOnly: true }}
              ></TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <KeyboardDateTimePicker
                variant="inline"
                ampm={false}
                autoOk
                inputVariant="outlined"
                format="yyyy-MM-dd HH:mm"
                margin="normal"
                style={{ width: "100%" }}
                label="Data i czas meczu"
                invalidDateMessage="Błędny format daty"
                minDate={new Date()}
                minDateMessage="Błędna data meczu"
                maxDate={league.endDate}
                maxDateMessage="Błędna data meczu"
                value={dateOfMatch}
                onChange={(e) => {
                  setDateOfMatch(e);
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                variant="outlined"
                style={{ width: "100%" }}
                margin="normal"
              >
                <InputLabel>Klub</InputLabel>
                <Select
                  onChange={(evt) => handleClubChange(evt)}
                  value={club?.id}
                  labelWidth={32}
                >
                  {league.type.access === LeagueAccess.Public ||
                    league.type.access === LeagueAccess.Tournament ? (
                      league.type.supportedClubs.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem
                        value={league.type.owner.id}
                        key={league.type.owner.id}
                      >
                        {league.type.owner.name}
                      </MenuItem>
                    )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {!club?.logoUrl || club?.name.startsWith("Inny") ? (
            <Alert severity="warning">
              Pamiętaj, że grając na obiektach, które nie współpracują ze
              SmartLigą, nie otrzymasz SmartPunktów za rozegrany mecz.
            </Alert>
          ) : (
              <Alert severity="info">
                Pamiętaj, aby mieć pewność, że otrzymasz zniżkę na korcie na mecz
                ligowy, Twój mecz musi zostać potwierdzony przynajmniej 24 godziny
                przed planowanym terminem.
            </Alert>
            )}

          {
            !!dateOfMatch && dateOfMatch >= league.endDate
              ? (<Alert severity="error">
                Data i godzina meczu jest późniejsza niż data i godzina zakończenia ligii.
            </Alert>) : (<></>)
          }
        </div>

        <DialogActions style={{ padding: "0px 24px 24px 24px" }}>
          <Button onClick={() => handleClose()}>Zamknij</Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={!!dateOfMatch && dateOfMatch >= league.endDate}
            variant="contained"
            color="primary"
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export default CreateMatchDialog;
